import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/**
 * Copyright IBM Corp. 2022, 2022
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { px } from '@carbon/layout';
import { pkg } from '../../settings';
import { usePreviousValue } from '../../global/js/hooks';
import { checkActiveHeaderCell } from './utils/checkActiveHeaderCell';
import { checkSelectedHeaderCell } from './utils/checkSelectedHeaderCell';
import { handleHeaderCellSelection } from './utils/handleHeaderCellSelection';
import { selectAllCells } from './utils/selectAllCells';
import { getSpreadsheetWidth } from './utils/getSpreadsheetWidth';
import { useSpreadsheetMouseMove } from './hooks';
var blockClass = "".concat(pkg.prefix, "--data-spreadsheet");
export var DataSpreadsheetHeader = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var activeCellCoordinates = _ref.activeCellCoordinates,
      cellSize = _ref.cellSize,
      columns = _ref.columns,
      currentMatcher = _ref.currentMatcher,
      defaultColumn = _ref.defaultColumn,
      headerGroups = _ref.headerGroups,
      scrollBarSize = _ref.scrollBarSize,
      selectionAreas = _ref.selectionAreas,
      setActiveCellCoordinates = _ref.setActiveCellCoordinates,
      setCurrentMatcher = _ref.setCurrentMatcher,
      setSelectionAreas = _ref.setSelectionAreas,
      setSelectionAreaData = _ref.setSelectionAreaData,
      rows = _ref.rows,
      totalVisibleColumns = _ref.totalVisibleColumns,
      updateActiveCellCoordinates = _ref.updateActiveCellCoordinates,
      setHeaderCellHoldActive = _ref.setHeaderCellHoldActive,
      headerCellHoldActive = _ref.headerCellHoldActive,
      selectAllAriaLabel = _ref.selectAllAriaLabel,
      visibleColumns = _ref.visibleColumns;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      scrollBarSizeValue = _useState2[0],
      setScrollBarSizeValue = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedHeaderReorderActive = _useState4[0],
      setSelectedHeaderReorderActive = _useState4[1];

  var previousState = usePreviousValue({
    cellSize: cellSize
  });
  useEffect(function () {
    if ((previousState === null || previousState === void 0 ? void 0 : previousState.cellSize) !== cellSize) {
      var _ref$current;

      var scrollContainer = ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.querySelector(".".concat(blockClass, "__list--container"));
      var hasScrollBar = (scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.scrollHeight) > (scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.clientHeight);
      var scrollBarValue = hasScrollBar ? 0 : scrollBarSize;
      setScrollBarSizeValue(scrollBarValue);
    }
  }, [cellSize, ref, scrollBarSize, previousState === null || previousState === void 0 ? void 0 : previousState.cellSize]);

  var handleColumnHeaderClick = function handleColumnHeaderClick(index) {
    return function (event) {
      var isHoldingCommandKey = event.metaKey || event.ctrlKey;
      var isHoldingShiftKey = event.shiftKey;
      handleHeaderCellSelection({
        type: 'column',
        activeCellCoordinates: activeCellCoordinates,
        rows: rows,
        columns: columns,
        setActiveCellCoordinates: setActiveCellCoordinates,
        setCurrentMatcher: setCurrentMatcher,
        setSelectionAreas: setSelectionAreas,
        spreadsheetRef: ref,
        index: index,
        setSelectionAreaData: setSelectionAreaData,
        isHoldingCommandKey: isHoldingCommandKey,
        isHoldingShiftKey: isHoldingShiftKey,
        currentMatcher: currentMatcher
      });
    };
  };

  var handleSelectAllClick = function handleSelectAllClick() {
    selectAllCells({
      ref: ref,
      setCurrentMatcher: setCurrentMatcher,
      setSelectionAreas: setSelectionAreas,
      rows: rows,
      columns: columns,
      activeCellCoordinates: activeCellCoordinates,
      updateActiveCellCoordinates: updateActiveCellCoordinates
    });
  };

  var handleHeaderMouseDown = function handleHeaderMouseDown(index) {
    return function (event) {
      var _selectionAreaToClone;

      if (event.shiftKey) {
        // Remove columns, need to call handleHeaderCellSelection
        return;
      }

      setSelectedHeaderReorderActive(true);
      var selectionAreaToClone = selectionAreas.filter(function (item) {
        return (item === null || item === void 0 ? void 0 : item.matcher) === currentMatcher;
      });
      var selectionAreaElement = ref.current.querySelector("[data-matcher-id=\"".concat((_selectionAreaToClone = selectionAreaToClone[0]) === null || _selectionAreaToClone === void 0 ? void 0 : _selectionAreaToClone.matcher, "\"]"));
      var clickXPosition = event.clientX;
      var headerButtonCoords = event.target.getBoundingClientRect();
      var headerIndex = event.target.getAttribute('data-column-index');
      var offsetXValue = clickXPosition - headerButtonCoords.left;
      var lowestColumnIndexFromSelectionArea = Math.min(selectionAreaToClone[0].point1.column, selectionAreaToClone[0].point2.column);
      var selectionAreaCoords = selectionAreaElement.getBoundingClientRect();
      var updatedOffsetDifference = lowestColumnIndexFromSelectionArea < parseInt(headerIndex) ? offsetXValue + (headerButtonCoords.left - selectionAreaCoords.left) : offsetXValue;
      var bodyContainer = document.querySelector(".".concat(blockClass, "__list--container")).firstElementChild;
      var selectionAreaClonedElement = selectionAreaElement.cloneNode();
      var reorderIndicatorLine = selectionAreaElement.cloneNode();
      reorderIndicatorLine.className = "".concat(blockClass, "__reorder-indicator-line");
      reorderIndicatorLine.style.width = px(2);
      selectionAreaClonedElement.classList.add("".concat(blockClass, "__selection-area--element-cloned"));
      selectionAreaClonedElement.setAttribute('data-clone-offset-x', updatedOffsetDifference);
      selectionAreaClonedElement.setAttribute('data-column-index-original', index);
      bodyContainer.appendChild(selectionAreaClonedElement);
      bodyContainer.appendChild(reorderIndicatorLine);
      setHeaderCellHoldActive(true);
    };
  };

  useSpreadsheetMouseMove({
    ref: ref,
    headerCellHoldActive: headerCellHoldActive,
    defaultColumn: defaultColumn
  });
  return /*#__PURE__*/React.createElement("div", {
    className: cx("".concat(blockClass, "__header--container")),
    role: "rowgroup"
  }, headerGroups.map(function (headerGroup, index) {
    return /*#__PURE__*/React.createElement("div", _extends({
      key: "header_".concat(index)
    }, headerGroup.getHeaderGroupProps(), {
      style: _objectSpread(_objectSpread({}, headerGroup.getHeaderGroupProps().style), {}, {
        width: getSpreadsheetWidth({
          type: 'header',
          headerGroup: headerGroup,
          scrollBarSizeValue: scrollBarSizeValue,
          totalVisibleColumns: totalVisibleColumns,
          defaultColumn: defaultColumn,
          visibleColumns: visibleColumns
        }),
        overflow: 'hidden'
      }),
      className: "".concat(blockClass, "__tr")
    }), /*#__PURE__*/React.createElement("div", {
      role: "columnheader",
      className: "".concat(blockClass, "__select-all-cell-container"),
      style: {
        width: defaultColumn === null || defaultColumn === void 0 ? void 0 : defaultColumn.rowHeaderWidth,
        height: defaultColumn === null || defaultColumn === void 0 ? void 0 : defaultColumn.rowHeight
      }
    }, /*#__PURE__*/React.createElement("button", {
      id: "".concat(blockClass, "__cell--header--header"),
      "data-row-index": "header",
      "data-column-index": "header",
      type: "button",
      tabIndex: -1,
      "aria-label": selectAllAriaLabel,
      onClick: handleSelectAllClick,
      className: cx("".concat(blockClass, "__th"), "".concat(blockClass, "--interactive-cell-element"), "".concat(blockClass, "__th--select-all"), _defineProperty({}, "".concat(blockClass, "__th--active-header"), (activeCellCoordinates === null || activeCellCoordinates === void 0 ? void 0 : activeCellCoordinates.column) === 'header' && (activeCellCoordinates === null || activeCellCoordinates === void 0 ? void 0 : activeCellCoordinates.row) === 'header'))
    }, "\xA0")), headerGroup.headers.map(function (column, index) {
      var _cx2;

      var selectedHeader = checkSelectedHeaderCell(index, selectionAreas, 'column', rows);
      return /*#__PURE__*/React.createElement("div", _extends({
        key: "column_".concat(index),
        role: "columnheader",
        className: "".concat(blockClass, "__columnheader")
      }, column.getHeaderProps()), /*#__PURE__*/React.createElement("button", {
        id: "".concat(blockClass, "__cell--header--").concat(index),
        "data-row-index": "header",
        "data-column-index": index,
        tabIndex: -1,
        onMouseDown: selectedHeader ? handleHeaderMouseDown(index) : null,
        onMouseUp: selectedHeader ? function () {
          return setSelectedHeaderReorderActive(false);
        } : null,
        onClick: !selectedHeader ? handleColumnHeaderClick(index) : null,
        style: {
          height: defaultColumn === null || defaultColumn === void 0 ? void 0 : defaultColumn.rowHeight,
          width: (column === null || column === void 0 ? void 0 : column.width) || (defaultColumn === null || defaultColumn === void 0 ? void 0 : defaultColumn.width)
        },
        className: cx("".concat(blockClass, "__th"), "".concat(blockClass, "--interactive-cell-element"), (_cx2 = {}, _defineProperty(_cx2, "".concat(blockClass, "__th--active-header"), (activeCellCoordinates === null || activeCellCoordinates === void 0 ? void 0 : activeCellCoordinates.column) === index || checkActiveHeaderCell(index, selectionAreas, 'column')), _defineProperty(_cx2, "".concat(blockClass, "__th--selected-header"), selectedHeader), _defineProperty(_cx2, "".concat(blockClass, "__th--selected-header-reorder-active"), selectedHeaderReorderActive), _cx2)),
        type: "button"
      }, column.render('Header')));
    }));
  }));
});
DataSpreadsheetHeader.propTypes = {
  /**
   * Object containing the active cell coordinates
   */
  activeCellCoordinates: PropTypes.shape({
    row: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    column: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),

  /**
   * Specifies the cell height
   */
  cellSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),

  /**
   * All of the spreadsheet columns
   */
  columns: PropTypes.array,

  /**
   * uuid that corresponds to the current selection area
   */
  currentMatcher: PropTypes.string,

  /**
   * Default spreadsheet sizing values
   */
  defaultColumn: PropTypes.shape({
    rowHeight: PropTypes.number,
    rowHeaderWidth: PropTypes.number,
    width: PropTypes.number
  }),

  /**
   * Whether or not a click/hold is active on a header cell
   */
  headerCellHoldActive: PropTypes.bool,

  /**
   * Headers provided from useTable hook
   */
  headerGroups: PropTypes.arrayOf(PropTypes.object),

  /**
   * All of the spreadsheet row data
   */
  rows: PropTypes.arrayOf(PropTypes.object),

  /**
   * The scrollbar width
   */
  scrollBarSize: PropTypes.number,

  /**
   * The aria label applied to the Select all button
   */
  selectAllAriaLabel: PropTypes.string.isRequired,

  /**
   * All of the cell selection area items
   */
  selectionAreas: PropTypes.arrayOf(PropTypes.object),

  /**
   * Setter fn for activeCellCoordinates value
   */
  setActiveCellCoordinates: PropTypes.func,

  /**
   * Setter fn for currentMatcher value
   */
  setCurrentMatcher: PropTypes.func,

  /**
   * Setter fn for header cell hold active value
   */
  setHeaderCellHoldActive: PropTypes.func,

  /**
   * Setter fn for selectionAreaData state value
   */
  setSelectionAreaData: PropTypes.func,

  /**
   * Setter fn for selectionAreas value
   */
  setSelectionAreas: PropTypes.func,

  /**
   * The total number of columns to be initially visible, additional columns will be rendered and
   * visible via horizontal scrollbar
   */
  totalVisibleColumns: PropTypes.number,

  /**
   * Function used to update the active cell coordinates
   */
  updateActiveCellCoordinates: PropTypes.func,

  /**
   * Array of visible columns provided by react-table useTable hook
   */
  visibleColumns: PropTypes.array
};